/**
 * InvestigationNode — read-through projection of a Workspace
 * `Investigation` rendered on the Environment surface.
 *
 * Cycle-102 sprint-3 Task 3.1.
 *
 * Renders the sprint-2 `NodeShapeRoot` in the `investigation` type
 * (pentagon, sig-mint) plus the 4 typed output ports per
 * `Environment_Architecture_v1.md §7.3`:
 *   - evidence_bundle_ref
 *   - claim_graph_state    (enum)
 *   - corroboration_score  ([0, 1])
 *   - readiness            (enum, NOT a float per SDD §3.3)
 *
 * Three render branches per SDD §6.4:
 *   1. Loading       → skeleton chip
 *   2. Redacted      → REDACTED placeholder + request-access CTA
 *                      (NEVER shows investigation metadata — SDD R-SDD-8)
 *   3. Resolved      → full summary + ports + double-click → Workspace
 *
 * Read-through only per PRD F-3.1 + SDD §1.4.3 — edit attempts on the
 * Environment-side node redirect to the Workspace investigation drawer
 * (the double-click handler).
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '../../../lib/utils';
import { NodeShapeRoot } from '../canvas/nodeShapes/NodeShapeRoot';
import {
  CLAIM_GRAPH_LABEL,
  READINESS_LABEL,
  type InvestigationProjectionPorts,
} from '../../../api/projectionClient';
import { useProjectionLink } from './useProjectionLink';

interface InvestigationNodeProps {
  investigationId: string;
  /** Centre X for the SVG node — defaults to a self-contained 40×40 frame. */
  cx?: number;
  cy?: number;
  radius?: number;
  className?: string;
}

const DEFAULT_R = 14;
const FRAME_PAD = 6;

export function InvestigationNode({
  investigationId,
  cx,
  cy,
  radius = DEFAULT_R,
  className,
}: InvestigationNodeProps) {
  const { isLoading, error, projection, refetch } = useProjectionLink(
    investigationId,
  );
  const navigate = useNavigate();

  const handleDoubleClick = useCallback(() => {
    if (projection?.summary?.workspace_url) {
      navigate(projection.summary.workspace_url);
    }
  }, [navigate, projection?.summary?.workspace_url]);

  const handleActivate = useCallback(() => {
    handleDoubleClick();
  }, [handleDoubleClick]);

  const frame = (radius + FRAME_PAD) * 2;
  const centerX = cx ?? radius + FRAME_PAD;
  const centerY = cy ?? radius + FRAME_PAD;

  // ── Loading ──────────────────────────────────────────────────────
  if (isLoading && !projection) {
    return (
      <div
        role="status"
        aria-label="Investigation node loading"
        className={cn(
          'inline-flex items-center gap-2 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-card)]/60 px-2 py-1',
          className,
        )}
      >
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--fg-muted)]">
          Loading…
        </span>
      </div>
    );
  }

  // ── Hard error (network / 4xx / 5xx) ─────────────────────────────
  if (error && !projection) {
    return (
      <div
        role="alert"
        data-testid={`investigation-node-${investigationId}-error`}
        className={cn(
          'inline-flex flex-col gap-1 rounded-md border border-amber-500/40 bg-amber-500/5 px-2 py-1',
          className,
        )}
      >
        <span className="text-[10px] font-mono uppercase tracking-widest text-amber-500">
          Bridge unavailable
        </span>
        <button
          type="button"
          onClick={() => refetch()}
          className="self-start text-[10px] font-mono text-[var(--fg-secondary)] underline"
        >
          retry
        </button>
      </div>
    );
  }

  // ── Redacted (cross-operator OR expired OR deleted) ──────────────
  if (projection?.redacted) {
    return (
      <div
        role="region"
        aria-label="Investigation node — access required"
        data-testid={`investigation-node-${investigationId}-redacted`}
        className={cn(
          'inline-flex flex-col gap-2 rounded-md border border-dashed border-[var(--border-subtle)] bg-[var(--bg-card)]/60 px-3 py-2',
          className,
        )}
      >
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-[var(--fg-dim)]/40" aria-hidden="true" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--fg-muted)]">
            Investigation
          </span>
        </div>
        <span className="text-[11px] font-mono text-[var(--fg-secondary)]">
          REDACTED — access required.
        </span>
        {projection.request_access_url && (
          <a
            href={projection.request_access_url}
            className="self-start px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded border border-[var(--border-subtle)] text-[var(--fg)] hover:bg-[var(--bg-hover)]
              focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sig-iris)]"
          >
            Request access
          </a>
        )}
      </div>
    );
  }

  // ── Resolved ────────────────────────────────────────────────────
  if (!projection?.summary) return null;

  const summary = projection.summary;
  const ports = summary.ports;

  return (
    <div
      data-testid={`investigation-node-${investigationId}`}
      onDoubleClick={handleDoubleClick}
      className={cn(
        'inline-flex flex-col gap-2 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-card)]/80 px-3 py-2',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <svg width={frame} height={frame}>
          <NodeShapeRoot
            id={`inv-${investigationId}`}
            type="investigation"
            status={statusFromReadiness(ports.readiness)}
            cx={centerX}
            cy={centerY}
            radius={radius}
            label={`Investigation node ${summary.headline}`}
            onActivate={handleActivate}
          />
        </svg>
        <div className="flex flex-col">
          <span className="text-[11px] font-mono text-[var(--fg)]">
            {summary.headline}
          </span>
          <span className="text-[9px] font-mono uppercase tracking-widest text-[var(--fg-muted)]">
            {summary.status}
          </span>
        </div>
      </div>
      <PortGrid ports={ports} />
    </div>
  );
}

function PortGrid({ ports }: { ports: InvestigationProjectionPorts }) {
  return (
    <dl
      aria-label="Investigation node ports"
      className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono"
    >
      <Port label="Readiness" value={READINESS_LABEL[ports.readiness]} />
      <Port
        label="Claim graph"
        value={CLAIM_GRAPH_LABEL[ports.claim_graph_state]}
      />
      <Port
        label="Corroboration"
        value={ports.corroboration_score.toFixed(2)}
      />
      <Port label="Bundle" value={truncateBundle(ports.evidence_bundle_ref)} />
    </dl>
  );
}

function Port({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[8px] uppercase tracking-widest text-[var(--fg-muted)]">
        {label}
      </dt>
      <dd className="text-[var(--fg-secondary)]">{value}</dd>
    </div>
  );
}

function truncateBundle(ref: string): string {
  if (ref.length <= 18) return ref;
  return `${ref.slice(0, 8)}…${ref.slice(-7)}`;
}

function statusFromReadiness(
  readiness: InvestigationProjectionPorts['readiness'],
): 'idle' | 'queued' | 'evaluating' | 'completed' | 'certified' {
  switch (readiness) {
    case 'INSUFFICIENT':
      return 'idle';
    case 'ADEQUATE':
      return 'queued';
    case 'STRONG':
      return 'evaluating';
    case 'READY_TO_PROMOTE':
      return 'certified';
    default:
      return 'idle';
  }
}
